import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import {
  bandRanges,
  cloudProblem,
  createPlatformPlan,
  getPlatformPlans,
  ladderProblem,
  platformPlanErrorText,
  withdrawPlatformPlan,
  type BandIn,
  type PlatformPlans,
} from "../../api/platformPlans";

/**
 * Platform › Platform plans (owner 2026-10-30): "I am not able to create new
 * plan with billing key number of subscriber and for cloud tenant there are
 * multiple key or parameter for billing CPU, RAM and Storage". superadmin
 * makes io.ankhos plans billed by a meter: a band ladder of its own, or a
 * cloud plan with included CU and GB.
 */
export function PlatformPlansScreen() {
  const identity = useIdentity();
  const qc = useQueryClient();
  const plans = useQuery({
    queryKey: ["platform-plans"],
    enabled: Boolean(identity.token),
    queryFn: ({ signal }) => getPlatformPlans(identity, signal),
    retry: false,
  });
  const [dialog, setDialog] = useState<"ladder" | "cloud" | null>(null);
  const [notice, setNotice] = useState("");
  const [withdrawing, setWithdrawing] = useState<WithdrawTarget | null>(null);
  const done = (text: string) => {
    setDialog(null);
    setNotice(text);
    void qc.invalidateQueries({ queryKey: ["platform-plans"] });
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h5" component="h1">
          Platform plans
        </Typography>
        <Typography variant="body2" color="text.secondary">
          io.ankhos plans billed by a meter. Plans from the catalog are shown read-only; plans you add here are marked “console”.
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button variant="contained" onClick={() => setDialog("ladder")}>
          New band ladder (by subscribers)
        </Button>
        <Button variant="outlined" onClick={() => setDialog("cloud")}>
          New cloud plan (by CU and GB)
        </Button>
      </Box>
      {notice && (
        <Alert severity="success" onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {plans.isLoading && <Typography variant="body2">Loading…</Typography>}
      {plans.error && <Alert severity="error">{platformPlanErrorText(plans.error)}</Alert>}
      {plans.data && <PlatformPlansView p={plans.data} onWithdraw={setWithdrawing} />}
      {dialog === "ladder" && <LadderDialog onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "cloud" && <CloudDialog onClose={() => setDialog(null)} onDone={done} />}
      {withdrawing && (
        <WithdrawDialog
          target={withdrawing}
          onClose={() => setWithdrawing(null)}
          onDone={(t) => {
            setWithdrawing(null);
            done(t);
          }}
        />
      )}
    </Box>
  );
}

export interface WithdrawTarget {
  name: string;
  body: { kind: "ladder"; family: string } | { kind: "cloud"; tier: string };
}

function WithdrawDialog({ target, onClose, onDone }: { target: WithdrawTarget; onClose: () => void; onDone: (t: string) => void }) {
  const identity = useIdentity();
  const go = useMutation({
    mutationFn: () => withdrawPlatformPlan(identity, target.body),
    onSuccess: () => onDone(`“${target.name}” withdrawn.`),
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Withdraw “{target.name}”?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          It can no longer be assigned to a tenant. Nothing is deleted and invoices already raised do not change. Its code stays taken. It is refused while a tenant is on it or is still to be billed on it.
        </Typography>
        {go.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {platformPlanErrorText(go.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" disabled={go.isPending} onClick={() => go.mutate()}>
          {go.isPending ? "Withdrawing…" : "Withdraw"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const rupees = (v?: number) => (v === undefined ? "—" : `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

/** The lists without hooks, so a test can render them. */
export function PlatformPlansView({ p, onWithdraw }: { p: PlatformPlans; onWithdraw?: (t: WithdrawTarget) => void }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" component="h2">
          Meters
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Meter</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>A month is</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {p.meters.map((m) => (
              <TableRow key={m.code}>
                <TableCell>{m.label}</TableCell>
                <TableCell>{m.unit}</TableCell>
                <TableCell>{m.aggregation}</TableCell>
                <TableCell>{m.measured ? <Chip size="small" color="success" label="measured" /> : <Chip size="small" label={m.code === "egress_gib" ? "billed from 01/12/2026" : "not metered yet"} />}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {p.ladders.map((l) => (
        <Paper key={l.family || "shared"} sx={{ p: 2 }} data-testid={`ladder-${l.family || "shared"}`}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography variant="h6" component="h2">
              {l.name}
            </Typography>
            <Chip size="small" label={l.source} />
            {l.family && <Typography variant="caption">ladder {l.family}</Typography>}
            {onWithdraw && l.source === "console" && l.bands.some((b) => b.active) && (
              <Button size="small" color="warning" onClick={() => onWithdraw({ name: l.name, body: { kind: "ladder", family: l.family } })}>
                Withdraw
              </Button>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Billed monthly in arrears at the band the tenant's average active subscribers fall in{l.family ? ", within this ladder only" : ""}. Prices excl. GST.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Band</TableCell>
                <TableCell>Active subscribers</TableCell>
                <TableCell>Per month</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {l.bands.map((b, i) => (
                <TableRow key={b.componentId || b.tier}>
                  <TableCell>
                    {b.name} {!b.active && <Chip size="small" label="withdrawn" />}
                  </TableCell>
                  <TableCell>{bandRanges(l.bands)[i]}</TableCell>
                  <TableCell>{b.price === undefined ? "negotiated" : rupees(b.price)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ))}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" component="h2">
          Cloud plans
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Base billed monthly in advance; use over the included amounts billed next month at the overage prices. Prices excl. GST.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Plan</TableCell>
              <TableCell>Per month</TableCell>
              <TableCell>Included CU</TableCell>
              <TableCell>Ceiling CU</TableCell>
              <TableCell>Block GB</TableCell>
              <TableCell>Data out GB</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {p.cloudPlans.map((c) => (
              <TableRow key={c.componentId || c.tier}>
                <TableCell>
                  {c.name} {!c.active && <Chip size="small" label="withdrawn" />}
                </TableCell>
                <TableCell>{rupees(c.price)}</TableCell>
                <TableCell>{c.cu}</TableCell>
                <TableCell>{c.ceilingCu ?? "—"}</TableCell>
                <TableCell>{c.blockStorageGb}</TableCell>
                <TableCell>{c.transferOutGb}</TableCell>
                <TableCell>
                  <Chip size="small" label={c.source} />
                  {onWithdraw && c.source === "console" && c.active && (
                    <Button size="small" color="warning" onClick={() => onWithdraw({ name: c.name, body: { kind: "cloud", tier: c.tier } })}>
                      Withdraw
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {p.overage.length > 0 && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Overage: {p.overage.map((o) => `${o.name} ${rupees(o.price)}`).join(" · ")}
          </Typography>
        )}
      </Paper>
    </Box>
  );
}

const todayIST = () => new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10);
const num = (s: string) => (s.trim() === "" ? NaN : Number(s));

interface BandRow {
  name: string;
  max: string;
  price: string;
}

function LadderDialog({ onClose, onDone }: { onClose: () => void; onDone: (t: string) => void }) {
  const identity = useIdentity();
  const [family, setFamily] = useState("");
  const [name, setName] = useState("");
  const [from, setFrom] = useState(todayIST());
  const [rows, setRows] = useState<BandRow[]>([
    { name: "", max: "", price: "" },
    { name: "", max: "", price: "" },
  ]);
  const bands: BandIn[] = rows.map((r) => ({ name: r.name.trim(), subscribersMax: r.max.trim() === "" ? undefined : num(r.max), price: num(r.price) }));
  const problem = ladderProblem(family, name, bands);
  const save = useMutation({
    mutationFn: () => createPlatformPlan(identity, { kind: "ladder", family, name: name.trim(), effectiveFrom: from, bands }),
    onSuccess: () => onDone(`Band ladder “${name.trim()}” created with ${bands.length} bands. Assign it to a client from Clients › Plan.`),
  });
  const set = (i: number, k: keyof BandRow, v: string) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>New band ladder (billed by active subscribers)</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography variant="body2">
          A tenant on this ladder pays, each month, the price of the band its average active subscribers fall in, using this ladder's bands only. Leave “up to”
          empty on the top band for “and more”.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
          <TextField size="small" label="Ladder name" value={name} onChange={(e) => setName(e.target.value)} />
          <TextField size="small" label="Code" value={family} onChange={(e) => setFamily(e.target.value.toLowerCase())} helperText="e.g. campus" />
          <TextField size="small" type="date" label="Prices from" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>
        {rows.map((r, i) => (
          <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ width: 110 }}>
              {bandRanges(bands)[i]}
            </Typography>
            <TextField size="small" label={`Band ${i + 1} name`} value={r.name} onChange={(e) => set(i, "name", e.target.value)} />
            <TextField size="small" label="Up to (subscribers)" value={r.max} onChange={(e) => set(i, "max", e.target.value.replace(/\D/g, ""))} inputProps={{ inputMode: "numeric" }} />
            <TextField size="small" label="Per month ₹ (excl. GST)" value={r.price} onChange={(e) => set(i, "price", e.target.value)} inputProps={{ inputMode: "decimal" }} />
            {rows.length > 1 && (
              <Button size="small" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
                Remove
              </Button>
            )}
          </Box>
        ))}
        {rows.length < 10 && (
          <Box>
            <Button size="small" onClick={() => setRows((rs) => [...rs, { name: "", max: "", price: "" }])}>
              Add band
            </Button>
          </Box>
        )}
        {problem && <Alert severity="info">{problem}</Alert>}
        {save.error && <Alert severity="error">{platformPlanErrorText(save.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={Boolean(problem) || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Creating…" : "Create ladder"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CloudDialog({ onClose, onDone }: { onClose: () => void; onDone: (t: string) => void }) {
  const identity = useIdentity();
  const [f, setF] = useState({ tier: "", name: "", price: "", cu: "", block: "", object: "0", transfer: "", ceiling: "", from: todayIST() });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((s) => ({ ...s, [k]: k === "tier" ? e.target.value.toLowerCase() : e.target.value }));
  const body = {
    tier: f.tier,
    name: f.name.trim(),
    price: num(f.price),
    cu: num(f.cu),
    blockStorageGb: f.block.trim() === "" ? 0 : num(f.block),
    objectStorageGb: f.object.trim() === "" ? 0 : num(f.object),
    transferOutGb: f.transfer.trim() === "" ? 0 : num(f.transfer),
    ceilingCu: f.ceiling.trim() === "" ? undefined : num(f.ceiling),
  };
  const problem = cloudProblem(body);
  const save = useMutation({
    mutationFn: () => createPlatformPlan(identity, { kind: "cloud", effectiveFrom: f.from, ...body }),
    onSuccess: () => onDone(`Cloud plan “${body.name}” created.`),
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New cloud plan (billed by CU and GB)</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography variant="body2">
          1 CU is one hour of the larger of vCPU or memory GiB ÷ 2. Use above the included amounts is billed next month at the overage prices. Data out is
          billed from 01/12/2026; object storage is not metered yet.
        </Typography>
        <TextField size="small" label="Plan name" value={f.name} onChange={set("name")} />
        <TextField size="small" label="Code" value={f.tier} onChange={set("tier")} helperText="e.g. build-plus" />
        <TextField size="small" label="Per month ₹ (excl. GST)" value={f.price} onChange={set("price")} inputProps={{ inputMode: "decimal" }} />
        <TextField size="small" label="Included compute (CU per month)" value={f.cu} onChange={set("cu")} inputProps={{ inputMode: "decimal" }} />
        <TextField size="small" label="Ceiling (CU per month, optional)" value={f.ceiling} onChange={set("ceiling")} inputProps={{ inputMode: "decimal" }} />
        <TextField size="small" label="Included block storage (GB)" value={f.block} onChange={set("block")} inputProps={{ inputMode: "decimal" }} />
        <TextField size="small" label="Included data out (GB) — billed from 01/12/2026" value={f.transfer} onChange={set("transfer")} inputProps={{ inputMode: "decimal" }} />
        <TextField size="small" label="Included object storage (GB) — not metered yet" value={f.object} onChange={set("object")} inputProps={{ inputMode: "decimal" }} />
        <TextField size="small" type="date" label="Price from" value={f.from} onChange={set("from")} InputLabelProps={{ shrink: true }} />
        {problem && <Alert severity="info">{problem}</Alert>}
        {save.error && <Alert severity="error">{platformPlanErrorText(save.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={Boolean(problem) || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Creating…" : "Create plan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
